import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from './context/ThemeContext';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { Home } from './pages/Home';
import { Login } from './pages/Login';
import { Register } from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import VerifyEmail from './pages/VerifyEmail';
import { Dashboard } from './pages/Dashboard';
import { QuizCreate } from './pages/QuizCreate';
import { QuizList } from './pages/QuizList';
import { QuizTake } from './pages/QuizTake';
import { QuizResults } from './pages/QuizResults';
import { CollectionManage } from './pages/CollectionManage';
import { About } from './pages/About';
import { Profile } from './pages/Profile';
import { Settings } from './pages/Settings';
import { Statistics } from './pages/Statistics';
import { Pricing } from './pages/Pricing';
import { PremiumDashboard } from './pages/PremiumDashboard';
import { PaymentSuccess } from './pages/PaymentSuccess';
import { PaymentCancel } from './pages/PaymentCancel';
import { useSelector } from 'react-redux';
import { RootState } from './store/store';
import './App.css';

// Auth guard component for premium routes
const PremiumRoute = ({ children }: { children: React.ReactElement }) => {
  const user = useSelector((state: RootState) => state.auth.user);
  return user?.isPremium ? children : <Navigate to="/dashboard" replace />;
};

// Auth guard component for settings route
const SettingsRoute = ({ children }: { children: React.ReactElement }) => {
  const user = useSelector((state: RootState) => state.auth.user);
  return user ? (user.isPremium ? children : <Navigate to="/dashboard" replace />) : <Navigate to="/login" replace />;
};

// Auth guard component for private routes
const PrivateRoute = ({ children }: { children: React.ReactElement }) => {
  const user = useSelector((state: RootState) => state.auth.user);
  return user ? children : <Navigate to="/login" state={{ from: window.location.pathname }} replace />;
};

// Auth guard for public routes (login/register)
const PublicRoute = ({ children }: { children: React.ReactElement }) => {
  const user = useSelector((state: RootState) => state.auth.user);
  return !user ? children : <Navigate to="/dashboard" replace />;
};

// Redirect dashboard to the appropriate version based on user status
const DashboardRedirect = () => {
  const user = useSelector((state: RootState) => state.auth.user);
  return <Navigate to={user?.isPremium ? "/premium" : "/dashboard"} replace />;
};

function App() {
  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || ''}>
      <ThemeProvider>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
          <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
          <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
          <Route path="/verify-email" element={<PublicRoute><VerifyEmail /></PublicRoute>} />
          <Route path="/quizzes" element={<PrivateRoute><QuizList /></PrivateRoute>} />
          <Route path="/quiz/create" element={<PrivateRoute><QuizCreate /></PrivateRoute>} />
          <Route path="/collections/:id" element={<PrivateRoute><CollectionManage /></PrivateRoute>} />
          <Route path="/quiz/:id" element={<PrivateRoute><QuizTake /></PrivateRoute>} />
          <Route path="/quiz/:id/results" element={<PrivateRoute><QuizResults /></PrivateRoute>} />
          <Route path="/about" element={<About />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/payment-success" element={<PaymentSuccess />} />
          <Route path="/payment-cancel" element={<PaymentCancel />} />
          <Route path="/profile" element={<PrivateRoute><Profile /></PrivateRoute>} />
          <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
          <Route path="/premium" element={<PremiumRoute><PremiumDashboard /></PremiumRoute>} />
          <Route path="/settings" element={<SettingsRoute><Settings /></SettingsRoute>} />
          <Route path="/statistics" element={<PremiumRoute><Statistics /></PremiumRoute>} />
          <Route path="/user" element={<DashboardRedirect />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </ThemeProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
